/**
 * The Tarabeeza brand: the palette and the mark.
 *
 * ترابيزة is the everyday word for *table*, so the mark is a table — a pedestal table drawn
 * plainly rather than a decorated letterform, so it survives being stamped on a receipt or
 * embroidered on an apron.
 *
 * Framework-free on purpose: React Native cannot render `<svg>`, so a shared React component
 * could only serve the three web apps. The mark is an SVG **string** instead — web drops it into
 * markup or a data URI, React Native hands it to `react-native-svg`'s `SvgXml`.
 */

/**
 * The palette as designed, at the exact values from the brand sheet.
 *
 * Where one of these fails contrast as TEXT, the corrected value lives in `brandText` below
 * rather than being changed here — the brand colour and the legible colour are two different
 * things.
 */
export const brandPalette = {
  /** Primary. The gold of the mark. */
  amber: '#C8852A',
  /** Highlight — the lit edge of a surface. */
  amberLight: '#E09A48',
  /** Shadow — the underside of a surface. */
  amberDark: '#9A6018',
  /** The tile the mark sits on. Warm, not neutral, black. */
  brown: '#1C1710',
  /** Page ground. Darker than the tile, so the tile reads as an object on it. */
  black: '#080604',
  /** Type on dark. */
  cream: '#F2EDE2',
  /** Secondary type and rules. See `brandText.muted` before using on text. */
  muted: '#7A6A4A',
} as const;

/**
 * Text colours, contrast-checked against `brandPalette.black`.
 *
 * The brand sheet's `muted` (#7A6A4A) measures **3.84:1** — AA for large text only, and it was
 * used on 7–9px labels. Two swatch-caption values, #4A3A22 and #3A2E1C, measure **1.85:1** and
 * **1.53:1**: invisible, not subtle. Staff read these screens at speed in a dim room, so the
 * small-text values are lifted to clear 4.5:1; the original stays as `brandPalette.muted` for
 * rules, borders and anything non-textual.
 */
export const brandText = {
  /** 17.33:1 — headings and body on the dark ground. */
  primary: brandPalette.cream,
  /** 5.80:1 — lifted from #7A6A4A (3.84:1) so small labels clear AA. */
  muted: '#9A8763',
  /** 6.59:1 — the accent, legible as text at body size. */
  accent: brandPalette.amber,
} as const;

/**
 * A 50–950 ramp built around `brandPalette.amber` at 500.
 *
 * The apps address tints by number (`brand-600`, `brand-50`), so three stops are not enough.
 * 500 is the brand colour exactly; the ends are extended, not invented — 700 is `amberDark`,
 * 400 is `amberLight`.
 */
export const brandRamp = {
  50: '#fdf6ec',
  100: '#f9e8cf',
  200: '#f2d0a0',
  300: '#e9b56c',
  400: '#E09A48',
  500: '#C8852A',
  600: '#af6f22',
  700: '#9A6018',
  800: '#7a4b16',
  900: '#5c3913',
  950: '#33200a',
} as const;

export interface MarkOptions {
  /** Rendered size in px. The artwork is authored at 100 and scales. */
  size?: number;
  /** Invert for a light ground: the tile becomes cream and the table dark. */
  light?: boolean;
  /**
   * Drop the inset ring on the tabletop.
   *
   * Its 0.8 stroke at the authored size is 0.3px on a 32px browser tab and 0.1px on a 16px
   * favicon — it renders as a grey smear rather than disappearing cleanly. Defaults off below
   * 48px via `tarabeezaMark`'s own guard; pass explicitly to override.
   */
  jewel?: boolean;
  /** Corner radius of the tile, 0 for a square lockup. */
  radius?: number;
}

/**
 * The mark, as an SVG string.
 *
 * The figure spans y=25..76 inside a 100 box: the 25px above and 24px below make it optically
 * centred rather than mathematically centred, since a table reads as sitting slightly high.
 */
export function tarabeezaMark(options: MarkOptions = {}): string {
  const { size = 100, light = false, radius = 22 } = options;
  // The ring is detail, not structure: below 48px it costs clarity.
  const jewel = options.jewel ?? size >= 48;

  const tile = light ? '#F0EBE0' : brandPalette.brown;
  const gold = light ? brandPalette.brown : brandPalette.amber;
  const goldLight = light ? '#3A2208' : brandPalette.amberLight;
  const goldDark = light ? '#1A0E04' : brandPalette.amberDark;

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 100 100" role="img" aria-label="Tarabeeza">`,
    `<rect x="0" y="0" width="100" height="100" rx="${radius}" fill="${tile}"/>`,
    // The tabletop's silhouette, used to clip its lit edge and its shadow. Without it the
    // square-cornered shadow spills past the rounded corners and leaves two dark nicks under
    // the tabletop at large sizes. The id is constant on purpose: the geometry never varies
    // with the options, so two marks on one page resolve to the same shape and cannot conflict.
    `<defs><clipPath id="tarabeeza-top"><rect x="11" y="25" width="78" height="14" rx="4"/></clipPath></defs>`,
    // Table top, with a lit edge and a shadowed underside.
    `<rect x="11" y="25" width="78" height="14" rx="4" fill="${gold}"/>`,
    `<g clip-path="url(#tarabeeza-top)">`,
    `<rect x="12" y="25" width="76" height="4" rx="4" fill="${goldLight}" opacity="0.38"/>`,
    `<rect x="11" y="36" width="78" height="3" fill="${goldDark}" opacity="0.28"/>`,
    `</g>`,
    // Pedestal.
    `<path d="M 44.5,39 L 55.5,39 L 53.5,66 L 46.5,66 Z" fill="${gold}"/>`,
    `<line x1="50" y1="39" x2="50" y2="66" stroke="${goldLight}" stroke-width="0.9" opacity="0.18"/>`,
    // Base.
    `<rect x="29" y="66" width="42" height="10" rx="3.5" fill="${gold}"/>`,
    `<rect x="30" y="73" width="40" height="3" rx="2" fill="${goldDark}" opacity="0.35"/>`,
    jewel
      ? `<circle cx="50" cy="32" r="4.5" fill="${tile}" fill-opacity="0.26"/>` +
        `<circle cx="50" cy="32" r="4.5" fill="none" stroke="${goldLight}" stroke-width="0.8" opacity="0.38"/>`
      : '',
    `</svg>`,
  ].join('');
}

/** The mark as a `data:` URI, for an `<img src>` or a CSS `background-image`. */
export function tarabeezaMarkDataUri(options: MarkOptions = {}): string {
  return `data:image/svg+xml,${encodeURIComponent(tarabeezaMark(options))}`;
}

export type BrandColor = keyof typeof brandPalette;
